import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Stepper, StepperContent, StepperActions } from '@/components/ui/stepper';
import { ArrowLeft, ArrowRight, Loader2, School, Rocket, ExternalLink } from 'lucide-react';
import {
  getSchoolOptions,
  createSchool,
  updateSchool,
  addDeployment,
  uploadSchoolFile,
  getSchoolForEdit,
} from '@/api/schools';
import { createEstablishment } from '@/api/establishments';
import type {
  SchoolOptions,
  CreateSchoolData,
  UpdateSchoolData,
  CreateSchoolResponse,
  SchoolEditData,
} from '@/types/school';
import type { V2DeployRequest } from '@/types/v2-deployment';
import {
  WizardFormData,
  WizardMode,
  initialFormData,
  WIZARD_STEPS,
  STEP_INFO,
  STEP_ESTABLISHMENTS,
  STEP_CONFIG,
  STEP_BRANDING,
  STEP_SUMMARY,
  STEP_DEPLOYMENT,
} from './types';
import {
  InfoStep,
  ConfigStep,
  DeploymentStep,
  BrandingStep,
  SummaryStep,
} from './components';
import { EstablishmentsStep } from './components/EstablishmentsStep';
import { V2DeployDialog } from '@/components/schools/V2DeployDialog';

interface SchoolWizardProps {
  mode: WizardMode;
  /** Requis en mode upgradeV2 : code de l'école V1 existante */
  schoolCode?: string;
  /** Chemin de retour à la fin du parcours (mode upgradeV2, ex. page projet migration) */
  returnTo?: string;
}

// Préremplissage du wizard depuis une école existante (mode passage V2)
function formDataFromSchool(school: SchoolEditData): WizardFormData {
  return {
    ...initialFormData,
    code: school.code,
    name: school.name,
    email: school.email || '',
    phone: school.phone || '',
    emailSender: school.emailSender || initialFormData.emailSender,
    domainSender: school.domainSender || '',
    address: school.address || '',
    addressComplement: school.addressComplement || '',
    addressHeadoffice: school.addressHeadoffice || '',
    addressHeadofficeComplement: school.addressHeadofficeComplement || '',
    networkId: school.networkId,
    // Rattachement déjà acquis (passage en V2) : on le reprend pour l'affichage,
    // il n'est jamais renvoyé — une école ne change pas de réseau physique.
    physicalNetworkId: school.physicalNetwork?.id ?? null,
    v2Host: school.v2Host || school.physicalNetwork?.v2Host || '',
    isEducationNationale: school.isEducationNationale ?? false,
    rne: school.rne || '',
    codeAcademie: school.codeAcademie || '',
    country: school.country || 'FR',
    latitude: school.latitude ?? null,
    longitude: school.longitude ?? null,
    type: school.type || initialFormData.type,
    support: school.support ?? initialFormData.support,
    vip: school.vip ?? false,
    retailerId: school.retailerId,
    technicalContactId: school.technicalContactId,
    salesContactId: school.salesContactId,
    whiteLabelEntityId: school.whiteLabelEntityId,
    // Le déploiement V2 est l'objet du parcours ; abonnement prérempli s'il existe déjà
    deployV2: {
      ...initialFormData.deployV2,
      enabled: true,
      offerId: school.v2Subscription?.offer?.id ?? null,
      optionIds: school.v2Subscription?.options?.map((o) => o.productOptionId) ?? [],
      moduleIds: school.v2Subscription?.moduleIds ?? [],
    },
  };
}

export function SchoolWizard({ mode, schoolCode, returnTo }: SchoolWizardProps) {
  const navigate = useNavigate();
  const isUpgrade = mode === 'upgradeV2';
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<WizardFormData>(initialFormData);
  const [options, setOptions] = useState<SchoolOptions | null>(null);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [codeValidationError, setCodeValidationError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isDeploying, setIsDeploying] = useState(false);
  const [creationResult, setCreationResult] = useState<CreateSchoolResponse | null>(null);
  const [creationProgress, setCreationProgress] = useState<{
    database: boolean | null;
    folder: boolean | null;
    informations: boolean | null;
  } | null>(null);
  const [v2DeployDialogOpen, setV2DeployDialogOpen] = useState(false);
  const [v2DeployConfig, setV2DeployConfig] = useState<V2DeployRequest | undefined>(undefined);

  // Mode passage V2 : école existante chargée pour préremplir le formulaire
  const [schoolData, setSchoolData] = useState<SchoolEditData | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    fetchOptions();
  }, []);

  const fetchSchool = useCallback(async () => {
    if (!isUpgrade || !schoolCode) return;
    setLoadError(null);
    try {
      const school = await getSchoolForEdit(schoolCode);
      setSchoolData(school);
      setFormData(formDataFromSchool(school));
    } catch (err) {
      console.error('Failed to load school:', err);
      setLoadError("Impossible de charger l'école");
    }
  }, [isUpgrade, schoolCode]);

  useEffect(() => {
    fetchSchool();
  }, [fetchSchool]);

  const fetchOptions = async () => {
    try {
      setLoadingOptions(true);
      const data = await getSchoolOptions();
      setOptions(data);
    } catch (err) {
      console.error('Failed to load options:', err);
    } finally {
      setLoadingOptions(false);
    }
  };

  const refreshOptions = async () => {
    try {
      const data = await getSchoolOptions();
      setOptions(data);
    } catch (err) {
      console.error('Failed to refresh options:', err);
    }
  };

  const handleChange = (data: Partial<WizardFormData>) => {
    setFormData((prev) => ({ ...prev, ...data }));
    // Clear errors for changed fields
    const changedKeys = Object.keys(data);
    setErrors((prev) => {
      const newErrors = { ...prev };
      changedKeys.forEach((key) => delete newErrors[key]);
      return newErrors;
    });
    // Clear code validation error when code changes
    if ('code' in data) {
      setCodeValidationError(null);
    }
  };

  const handleCodeValidation = (isValid: boolean, error?: string) => {
    setCodeValidationError(error || null);
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    switch (step) {
      case STEP_INFO:
        if (!formData.name.trim()) {
          newErrors.name = 'Le nom est requis';
        }
        if (!formData.code.trim()) {
          newErrors.code = 'Le code est requis';
        } else if (!isUpgrade) {
          if (!/^[A-Z0-9]+$/.test(formData.code)) {
            newErrors.code =
              'Le code ne doit contenir que des lettres majuscules et chiffres';
          } else if (codeValidationError) {
            newErrors.code = codeValidationError;
          }
        }
        if (!formData.emailSender.trim()) {
          newErrors.emailSender = "L'email expediteur est requis";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailSender)) {
          newErrors.emailSender = "L'email expediteur n'est pas valide";
        }
        // Passage V2 : les données poussées vers le tenant doivent être qualifiées
        if (isUpgrade) {
          if (!formData.address.trim()) {
            newErrors.address = "L'adresse est requise pour le déploiement V2";
          }
          if (formData.isEducationNationale) {
            if (!formData.rne.trim()) {
              newErrors.rne = 'Le RNE est requis pour une école Éducation Nationale';
            } else if (!/^\d{7}[A-Z]$/.test(formData.rne)) {
              newErrors.rne =
                'Le RNE doit être au format UAI : 7 chiffres suivis d\'une lettre majuscule';
            }
            if (!formData.codeAcademie.trim()) {
              newErrors.codeAcademie =
                "L'académie est requise pour une école Éducation Nationale";
            }
          }
        }
        break;

      // Other steps don't have required fields
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < WIZARD_STEPS.length - 1) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (stepIndex: number) => {
    // Only allow going back to previous steps (but not after creation)
    if (stepIndex < currentStep && !creationResult) {
      setCurrentStep(stepIndex);
    }
  };

  // Upload des fichiers de branding sélectionnés dans le wizard
  const uploadBrandingFiles = async (code: string) => {
    const uploads: Array<['logo' | 'logoSignin' | 'favicon' | 'cover', File | null]> = [
      ['logo', formData.logoFile],
      ['logoSignin', formData.logoSigninFile],
      ['favicon', formData.faviconFile],
      ['cover', formData.coverFile],
    ];
    await Promise.all(
      uploads
        .filter(([, file]) => file !== null)
        .map(([field, file]) => uploadSchoolFile(code, field, file as File).then(() => {}))
    );
  };

  // Create school WITHOUT deployment
  const handleCreate = async () => {
    if (!validateStep(currentStep)) return;

    setIsCreating(true);

    try {
      // Prepare create data WITHOUT deployment
      const createData: CreateSchoolData = {
        code: formData.code,
        name: formData.name,
        emailSender: formData.emailSender,
        email: formData.email || undefined,
        phone: formData.phone || undefined,
        address: formData.address || undefined,
        addressComplement: formData.addressComplement || undefined,
        addressHeadoffice: formData.addressHeadoffice || undefined,
        addressHeadofficeComplement: formData.addressHeadofficeComplement || undefined,
        domainSender: formData.domainSender || undefined,
        type: formData.type || undefined,
        support: formData.support || undefined,
        vip: formData.vip,

        networkId: formData.networkId || undefined,
        // Réseau physique : l'école naît dedans, donc son tenant V2 sera créé
        // directement dans le tenant group du réseau (avec sa marque blanche).
        // Le choix dans le formulaire vaut confirmation du caractère définitif,
        // l'avertissement étant affiché à côté du sélecteur.
        physicalNetworkId: formData.physicalNetworkId || undefined,
        v2Host: formData.physicalNetworkId ? formData.v2Host || undefined : undefined,
        confirmNetworkAttachment: formData.physicalNetworkId ? true : undefined,
        retailerId: formData.retailerId || undefined,
        technicalContactId: formData.technicalContactId || undefined,
        salesContactId: formData.salesContactId || undefined,
        isEducationNationale: formData.isEducationNationale,
        rne: formData.rne || undefined,
        codeAcademie: formData.codeAcademie || undefined,
        country: formData.country || 'FR',
        latitude: formData.latitude,
        longitude: formData.longitude,
        // No deployment at creation
        deployV1: false,
        deployV2: false,
      };

      // Create school
      const result = await createSchool(createData);
      setCreationResult(result);

      const schoolCode = result.code;
      await uploadBrandingFiles(schoolCode);

      // Les établissements secondaires saisis à l'étape Établissements
      // (le principal est créé automatiquement par le backend)
      if (formData.establishments.length > 0) {
        const results = await Promise.allSettled(
          formData.establishments.map((draft) =>
            createEstablishment(schoolCode, {
              name: draft.name,
              isPrincipal: false,
              rne: draft.rne || null,
              codeAcademie: draft.codeAcademie || null,
              address: draft.address || null,
              addressComplement: draft.addressComplement || null,
              latitude: draft.latitude,
              longitude: draft.longitude,
              contractType: draft.contractType || null,
            })
          )
        );
        const failed = results.filter((r) => r.status === 'rejected').length;
        if (failed > 0) {
          setErrors({
            submit: `L'école a été créée mais ${failed} établissement(s) n'ont pas pu être créés. Vous pourrez les ajouter depuis la fiche école.`,
          });
        }
      }

      setIsCreating(false);
      // Move to deployment step
      setCurrentStep(STEP_DEPLOYMENT);
    } catch (err) {
      console.error('Failed to create school:', err);
      setCreationResult(null);
      setIsCreating(false);
      setErrors({
        submit:
          err instanceof Error
            ? err.message
            : "Erreur lors de la création de l'école",
      });
    }
  };

  // Mode passage V2 : mise à jour de l'école existante avant déploiement
  const handleUpdate = async () => {
    if (!validateStep(currentStep) || !schoolCode || !schoolData) return;

    setIsCreating(true);

    try {
      const updateData: UpdateSchoolData = {
        name: formData.name,
        email: formData.email || null,
        phone: formData.phone || null,
        emailSender: formData.emailSender,
        domainSender: formData.domainSender || null,
        address: formData.address || null,
        addressComplement: formData.addressComplement || null,
        addressHeadoffice: formData.addressHeadoffice || null,
        addressHeadofficeComplement: formData.addressHeadofficeComplement || null,
        type: formData.type || null,
        support: formData.support || null,
        vip: formData.vip,
        networkId: formData.networkId,
        retailerId: formData.retailerId,
        technicalContactId: formData.technicalContactId,
        salesContactId: formData.salesContactId,
        isEducationNationale: formData.isEducationNationale,
        rne: formData.rne || null,
        codeAcademie: formData.codeAcademie || null,
        country: formData.country || 'FR',
        latitude: formData.latitude,
        longitude: formData.longitude,
      };
      // Marque blanche : plus jamais envoyée — le lien V1 legacy reste tel
      // quel et le rattachement V2 passe par les réseaux physiques.
      await updateSchool(schoolCode, updateData);
      await uploadBrandingFiles(schoolCode);

      setCreationResult({
        id: schoolData.id,
        code: schoolData.code,
        name: formData.name,
        hasV1: schoolData.hasV1 ?? false,
        hasV2: schoolData.hasV2 ?? false,
        workflow: {},
        message: 'École mise à jour',
      });
      setIsCreating(false);
      setCurrentStep(STEP_DEPLOYMENT);
    } catch (err) {
      console.error('Failed to update school:', err);
      setIsCreating(false);
      setErrors({
        submit:
          err instanceof Error
            ? err.message
            : "Erreur lors de la mise à jour de l'école",
      });
    }
  };

  // Deploy the created school
  const handleDeploy = async () => {
    if (!creationResult) return;

    // Handle V2 deployment via dialog
    if (formData.deployV2.enabled) {
      // Le tenant group cible (réseau physique / défaut) est résolu dans le
      // V2DeployDialog, plus jamais figé ici.
      const config: V2DeployRequest = {
        offerId: formData.deployV2.offerId || undefined,
        optionIds: formData.deployV2.optionIds.length > 0 ? formData.deployV2.optionIds : undefined,
        moduleIds: formData.deployV2.moduleIds.length > 0 ? formData.deployV2.moduleIds : undefined,
      };
      setV2DeployConfig(config);
      setV2DeployDialogOpen(true);
      return;
    }

    // Handle V1 deployment only (no V2)
    if (formData.deployV1.enabled) {
      setIsDeploying(true);
      setCreationProgress({
        database: null,
        folder: null,
        informations: null,
      });

      try {
        const result = await addDeployment(creationResult.code, {
          version: 'v1',
          status: formData.deployV1.status,
          schoolSourceId: formData.deployV1.schoolSourceId || undefined,
          moduleIds: formData.deployV1.moduleIds,
        });
        const v1Workflow = result.deployment?.workflow;
        setCreationProgress({
          database: v1Workflow?.database ?? null,
          folder: v1Workflow?.folder ?? null,
          informations: v1Workflow?.informations ?? null,
        });

        // Update creation result to reflect V1 deployment
        setCreationResult({
          ...creationResult,
          hasV1: true,
          hasV2: false,
        });
      } catch (err) {
        console.error('Failed to deploy school:', err);
        setCreationProgress(null);
        setIsDeploying(false);
        setErrors({
          submit:
            err instanceof Error
              ? err.message
              : "Erreur lors du déploiement de l'école",
        });
      }
    }
  };

  const handleV2DeploySuccess = () => {
    // Update creation result to reflect V2 deployment
    if (creationResult) {
      setCreationResult({
        ...creationResult,
        hasV2: true,
      });
    }
  };

  const handleGoToSchool = () => {
    if (creationResult) {
      navigate(`/schools/${creationResult.code}`);
    }
  };

  const handleGoBack = () => {
    if (isUpgrade) {
      navigate(returnTo || (schoolCode ? `/schools/${schoolCode}` : '/schools'));
    } else {
      navigate('/schools');
    }
  };

  const backLink = isUpgrade
    ? returnTo || (schoolCode ? `/schools/${schoolCode}` : '/schools')
    : '/schools';

  const renderStepContent = () => {
    switch (currentStep) {
      case STEP_INFO:
        return (
          <InfoStep
            formData={formData}
            onChange={handleChange}
            options={options}
            errors={errors}
            onCodeValidation={handleCodeValidation}
            codeReadOnly={isUpgrade}
            physicalNetworkEditable={!isUpgrade}
          />
        );
      case STEP_ESTABLISHMENTS:
        return (
          <EstablishmentsStep
            mode={mode}
            formData={formData}
            onChange={handleChange}
            schoolCode={schoolCode}
            hasV1={schoolData?.hasV1 ?? false}
            hasV2={schoolData?.hasV2 ?? false}
          />
        );
      case STEP_CONFIG:
        return (
          <ConfigStep
            formData={formData}
            onChange={handleChange}
            options={options}
          />
        );
      case STEP_BRANDING:
        return (
          <BrandingStep
            formData={formData}
            onChange={handleChange}
            options={options}
            onRefreshOptions={refreshOptions}
          />
        );
      case STEP_SUMMARY:
        return (
          <SummaryStep
            formData={formData}
            options={options}
            isCreating={isCreating}
            mode={mode}
          />
        );
      case STEP_DEPLOYMENT:
        return (
          <DeploymentStep
            formData={formData}
            onChange={handleChange}
            options={options}
            isDeploying={isDeploying}
            creationResult={creationResult}
            creationProgress={creationProgress || undefined}
            v2Only={isUpgrade}
          />
        );
      default:
        return null;
    }
  };

  if (loadingOptions || (isUpgrade && !schoolData && !loadError)) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isUpgrade && loadError) {
    return (
      <div className="space-y-4">
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {loadError}
        </div>
        <Button variant="outline" onClick={fetchSchool}>
          Réessayer
        </Button>
      </div>
    );
  }

  const isSummaryStep = currentStep === STEP_SUMMARY;
  const isDeploymentStep = currentStep === STEP_DEPLOYMENT;
  const hasDeploymentSelected = formData.deployV1.enabled || formData.deployV2.enabled;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon-sm" asChild>
              <Link to={backLink}>
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              {isUpgrade ? <Rocket className="h-5 w-5" /> : <School className="h-5 w-5" />}
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
              {isUpgrade ? `Passage en V2 — ${schoolData?.name ?? schoolCode}` : 'Nouvelle école'}
            </h1>
          </div>
          {isUpgrade && (
            <p className="text-sm text-muted-foreground ml-[52px]">
              Vérifiez et complétez les informations reprises de la V1, puis lancez le déploiement V2.
            </p>
          )}
        </div>
      </div>

      {/* Error message */}
      {errors.submit && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {errors.submit}
        </div>
      )}

      {/* Stepper */}
      <Card>
        <CardContent className="pt-6">
          <Stepper
            steps={WIZARD_STEPS}
            currentStep={currentStep}
            onStepClick={handleStepClick}
          />

          <StepperContent>{renderStepContent()}</StepperContent>

          <StepperActions>
            {/* Previous button - hidden on deployment step after creation */}
            {!(isDeploymentStep && creationResult) && (
              <Button
                type="button"
                variant="outline"
                onClick={handlePrevious}
                disabled={currentStep === 0 || isCreating}
              >
                <ArrowLeft className="h-4 w-4" />
                Precedent
              </Button>
            )}

            {/* Summary step actions */}
            {isSummaryStep && !creationResult && (
              <Button
                type="button"
                onClick={isUpgrade ? handleUpdate : handleCreate}
                disabled={isCreating}
              >
                {isCreating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {isUpgrade ? 'Enregistrement...' : 'Creation...'}
                  </>
                ) : (
                  <>
                    <School className="h-4 w-4" />
                    {isUpgrade ? 'Enregistrer et continuer' : "Créer l'école"}
                  </>
                )}
              </Button>
            )}

            {/* Deployment step actions */}
            {isDeploymentStep && creationResult && (
              <div className="flex gap-2 ml-auto">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleGoBack}
                >
                  {isUpgrade && returnTo ? 'Retour à la migration' : 'Retour à la liste'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleGoToSchool}
                >
                  <ExternalLink className="h-4 w-4" />
                  Voir la fiche
                </Button>
                {hasDeploymentSelected && !isDeploying && !creationProgress && (
                  <Button
                    type="button"
                    onClick={handleDeploy}
                  >
                    <Rocket className="h-4 w-4" />
                    Deployer
                  </Button>
                )}
              </div>
            )}

            {/* Normal next button for other steps */}
            {!isSummaryStep && !isDeploymentStep && (
              <Button type="button" onClick={handleNext}>
                Suivant
                <ArrowRight className="h-4 w-4" />
              </Button>
            )}
          </StepperActions>
        </CardContent>
      </Card>

      {/* V2 Deployment Dialog */}
      {creationResult && (
        <V2DeployDialog
          open={v2DeployDialogOpen}
          onOpenChange={setV2DeployDialogOpen}
          schoolCode={creationResult.code}
          schoolName={formData.name}
          deployConfig={v2DeployConfig}
          requiresPhysicalNetwork={!!formData.whiteLabelEntityId}
          hasV1={isUpgrade ? schoolData?.hasV1 ?? false : false}
          onSuccess={handleV2DeploySuccess}
        />
      )}
    </div>
  );
}
